/* Oricle 9/29 port behaviours, one custom element per design component, each self-contained and re-initialised by the
   theme editor (a re-rendered section connects a fresh element). Loaded by the sections that use them; every define is
   guarded, so a second copy of this script on the page is a no-op.
   · oricle-tapviews      TapViews (the wear section): numbered views on a WAI tablist, the chosen view's photograph
   · oricle-compare-tabs  CompareTabs: WAI tabs, arrow keys move and select
   · oricle-cliprail      ClipRail: a tap swaps the poster for an inline <video controls>, one plays at a time
   · oricle-clamp         ReviewCard "Read more": shown only when the text actually overflows its clamp
   · oricle-offer-dock    StickyBuyDock offer: in once the watched section is above the header, out at the footer
   · oricle-press-row     PressStrip: still while the marks fit on one line, a slow CSS loop (is-marquee) when not */
(() => {
  if (window.OricleComponents) return;
  window.OricleComponents = true;
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };

  /* · TapViews (TapViews.prompt.md, W2 9/30; replaced FeatureDisclosure). A WAI tablist with one tab stop: arrow keys
     move and select (Right/Down next, Left/Up previous, wrapping), Home/End jump, and each panel is labelled by its tab.
     The panel swap is instant. The other views' photographs are fetched once the section is near the viewport, so a
     tap never waits on the network. · */
  class OricleTapViews extends HTMLElement {
    connectedCallback() {
      if (this._bound) return; this._bound = true;
      this.tabs = Array.from(this.querySelectorAll('[data-o-tv-tab]'));
      if (!this.tabs.length) return;
      this.tabs.forEach((t, i) => t.addEventListener('click', () => this.select(i)));
      const list = this.querySelector('[data-o-tv-list]');
      if (list) list.addEventListener('keydown', (e) => {
        const cur = this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
        const n = this.tabs.length;
        let next = -1;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (cur + 1) % n;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (cur - 1 + n) % n;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = n - 1;
        if (next < 0) return;
        e.preventDefault();
        this.select(next);
        this.tabs[next].focus();
      });
      const warm = () => this.querySelectorAll('.o-tv__img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((es) => { if (es.some((x) => x.isIntersecting)) { warm(); this.io.disconnect(); } }, { rootMargin: '300px 0px' });
        this.io.observe(this);
      } else warm();
    }
    disconnectedCallback() { if (this.io) this.io.disconnect(); }
    select(i) {
      this.tabs.forEach((t, k) => {
        const on = k === i;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        const p = document.getElementById(t.getAttribute('aria-controls'));
        if (p) p.hidden = !on;
      });
      const cap = this.querySelector('[data-o-tv-cap]');
      if (cap) { const txt = (this.tabs[i] && this.tabs[i].dataset.cap) || ''; cap.textContent = txt; cap.hidden = !txt; }
    }
  }
  define('oricle-tapviews', OricleTapViews);

  /* · CompareTabs (CompareTabs.prompt.md): tabs are role=tab with a roving tabindex; Left/Right wrap, Home/End jump, and
     each move selects (the panel swap is instant). · */
  class OricleCompareTabs extends HTMLElement {
    connectedCallback() {
      if (this._bound) return; this._bound = true;
      this.tabs = Array.from(this.querySelectorAll('[data-o-ctab]'));
      this.tabs.forEach((t, i) => t.addEventListener('click', () => this.select(i)));
      const list = this.querySelector('[role="tablist"]');
      if (list) list.addEventListener('keydown', (e) => {
        const cur = this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
        const n = this.tabs.length;
        let next = -1;
        if (e.key === 'ArrowRight') next = (cur + 1) % n;
        else if (e.key === 'ArrowLeft') next = (cur - 1 + n) % n;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = n - 1;
        if (next < 0) return;
        e.preventDefault();
        this.select(next);
        this.tabs[next].focus();
      });
    }
    select(i) {
      this.tabs.forEach((t, k) => {
        const on = k === i;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        const p = document.getElementById(t.getAttribute('aria-controls'));
        if (p) p.hidden = !on;
      });
      const t = this.tabs[i];
      if (t && t.scrollIntoView && t.parentElement && t.parentElement.scrollWidth > t.parentElement.clientWidth) t.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }
  define('oricle-compare-tabs', OricleCompareTabs);

  /* · ClipRail (ClipRail.prompt.md): nothing autoplays. A clip with a video (a <template> beside its poster) plays on tap:
     the poster button is swapped for the video with controls, focus moves to it, and any other playing clip goes back to
     its poster. A clip without a video is a still with no control at all. · */
  class OricleClipRail extends HTMLElement {
    connectedCallback() {
      if (this._bound) return; this._bound = true;
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-o-clip-play]');
        if (!btn || !this.contains(btn)) return;
        this.play(btn.closest('[data-o-clip]'));
      });
    }
    reset(card) {
      const v = card.querySelector('video');
      if (v) { v.pause(); v.remove(); }
      const btn = card.querySelector('[data-o-clip-play]');
      if (btn) btn.hidden = false;
      card.classList.remove('is-playing');
    }
    play(card) {
      if (!card) return;
      this.querySelectorAll('[data-o-clip].is-playing').forEach((c) => { if (c !== card) this.reset(c); });
      const tpl = card.querySelector('template');
      const btn = card.querySelector('[data-o-clip-play]');
      if (!tpl || !btn) return;
      const frag = tpl.content.cloneNode(true);
      const v = frag.querySelector('video');
      if (!v) return;
      v.controls = true; v.playsInline = true; v.setAttribute('playsinline', '');
      v.classList.add('o-clip2__video');
      v.addEventListener('ended', () => this.reset(card));
      btn.hidden = true;
      card.classList.add('is-playing');
      card.querySelector('.o-clip2__frame').appendChild(v);
      v.focus();
      const p = v.play(); if (p && p.catch) p.catch(() => {});
    }
  }
  define('oricle-cliprail', OricleClipRail);

  /* · Review clamp (ReviewCard.prompt.md): long bodies clamp to 6 lines (5 on a phone) in CSS; the toggle appears only when
     the text overflows. Re-measured on resize and when a hidden card is revealed (Show more, a chip). · */
  class OricleClamp extends HTMLElement {
    connectedCallback() {
      if (this._bound) return; this._bound = true;
      this.text = this.querySelector('[data-o-clamp-text]');
      this.btn = this.querySelector('[data-o-clamp-btn]');
      if (!this.text || !this.btn) return;
      this.btn.addEventListener('click', () => {
        const open = !this.classList.contains('is-open');
        this.classList.toggle('is-open', open);
        this.btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        const lbl = this.btn.querySelector('[data-o-clamp-label]') || this.btn.querySelector('span');
        if (lbl) lbl.textContent = open ? (this.btn.dataset.less || this.btn.dataset.more) : this.btn.dataset.more;
        if (!open) this.check();
      });
      this.check = () => { if (!this.classList.contains('is-open')) this.btn.hidden = !(this.text.scrollHeight > this.text.clientHeight + 1); };
      this.check();
      // A clamped box keeps its height when the web font swaps in, so the observer alone would miss it.
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => { if (this.isConnected) this.check(); });
      if ('ResizeObserver' in window) { this.ro = new ResizeObserver(() => this.check()); this.ro.observe(this.text); }
    }
    disconnectedCallback() { if (this.ro) this.ro.disconnect(); }
  }
  define('oricle-clamp', OricleClamp);

  /* · Offer dock (StickyBuyDock.prompt.md, variant offer; HomePage.jsx 9/28). Visible once the watched section's bottom
     edge has scrolled above the header, hidden the moment the footer's top edge is in view (so it never covers footer
     links) and while any drawer is open. Hidden, it is inert and unfocusable. Measured once per animation frame on
     scroll and resize; reduced motion shows it without the slide (CSS). · */
  class OricleOfferDock extends HTMLElement {
    connectedCallback() {
      this.drawer = false;
      this.onOpen = () => { this.drawer = true; this.update(); };
      this.onClose = () => { this.drawer = false; this.update(); };
      document.addEventListener('oricle:drawer:open', this.onOpen);
      document.addEventListener('oricle:drawer:close', this.onClose);
      let ticking = false;
      this.onScroll = () => { if (ticking) return; ticking = true; requestAnimationFrame(() => { ticking = false; this.update(); }); };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      this.update();
    }
    disconnectedCallback() {
      document.removeEventListener('oricle:drawer:open', this.onOpen);
      document.removeEventListener('oricle:drawer:close', this.onClose);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }
    update() {
      const target = document.querySelector(this.dataset.watch || '#lineup');
      const header = document.querySelector('oricle-header');
      const footer = document.querySelector('footer.o-ftr') || document.querySelector('footer');
      const top = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
      const past = !!target && target.getBoundingClientRect().bottom < top;
      const atFooter = !!footer && footer.getBoundingClientRect().top < window.innerHeight;
      // data-hide (the advertorial's offer boxes, 10/7): out while that element is on screen, so the bar never
      // sits over the boxes it points to.
      const hideEl = this.dataset.hide ? document.querySelector(this.dataset.hide) : null;
      const hr = hideEl ? hideEl.getBoundingClientRect() : null;
      const atHide = !!hr && hr.top < window.innerHeight && hr.bottom > top;
      const on = past && !atFooter && !atHide && !this.drawer;
      if (on === this._on) return;
      this._on = on;
      this.classList.toggle('is-visible', on);
      this.setAttribute('aria-hidden', on ? 'false' : 'true');
      if (on) this.removeAttribute('inert'); else this.setAttribute('inert', '');
    }
  }
  define('oricle-offer-dock', OricleOfferDock);
  /* · PressStrip (snippets/oricle-press-row.liquid, Jackson 10/9). The marks' natural one-line width comes from their
     reserved boxes (the CSS height times each image's width/height attributes), so it reads the same whether the row
     is still or running and before any logo has loaded. Wider than the row by more than 6% (within that the line
     shrinks its marks to fit): is-marquee, with the loop timed to about 35px a second; otherwise is-fit, which also
     stops the loop the snippet's container query may have started before this ran. The animation is CSS only, and
     reduced motion is CSS's call (nothing moves, the marks wrap into rows), so nothing is measured then. · */
  class OriclePressRow extends HTMLElement {
    connectedCallback() {
      if (this._ro) return;
      this.set = this.querySelector('.o-ctpress__marks');
      if (!this.set || !('ResizeObserver' in window)) return;
      this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._update = () => this.update();
      this._ro = new ResizeObserver(this._update);
      this._ro.observe(this);
      if (this.reduce.addEventListener) this.reduce.addEventListener('change', this._update);
      this.update();
    }
    disconnectedCallback() {
      if (this._ro) this._ro.disconnect();
      if (this.reduce && this.reduce.removeEventListener) this.reduce.removeEventListener('change', this._update);
      this._ro = null;
    }
    update() {
      if (this.reduce.matches) { this.classList.remove('is-marquee', 'is-fit'); return; }
      const marks = this.set.querySelectorAll('.o-ctpress__logo, .o-ctpress__name');
      const gap = parseFloat(getComputedStyle(this.set).columnGap) || 0;
      let width = gap * Math.max(0, marks.length - 1);
      marks.forEach((m) => {
        const w = Number(m.getAttribute('width')); const h = Number(m.getAttribute('height'));
        width += m.tagName === 'IMG' && w && h ? m.getBoundingClientRect().height * w / h : m.scrollWidth;
      });
      const run = width > this.clientWidth * 1.06;
      this.classList.toggle('is-marquee', run);
      this.classList.toggle('is-fit', !run);
      if (run) this.style.setProperty('--press-loop', ((width + gap) / 35).toFixed(1) + 's');
    }
  }
  define('oricle-press-row', OriclePressRow);
})();
